import { Dialog, Flex, VStack, Text, Button, Input, LucideIcon } from '@skin-seoul/design-system';
import { useState } from 'react';

interface TrackOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TrackOrderModal = ({ isOpen, onClose }: TrackOrderModalProps) => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');

  const handleTrackOrder = (e: React.FormEvent) => {
    e.preventDefault();

    const params = new URLSearchParams({
      email: email.trim(),
      orderNumber: orderNumber.trim(),
    });

    window.location.href = `/delivery-tracking/?${params.toString()}`;
  };

  const isFormValid = orderNumber.trim() !== '' && email.trim() !== '';

  return (
    <Dialog.Root open={isOpen} preventScroll>
      <Dialog.Backdrop backgroundColor="generic.100/40" backdropFilter="blur(4px)" />
      <Dialog.Positioner>
        <Dialog.Content
          maxWidth={{ base: '100%', tablet: '800px', desktop: '900px' }}
          width="100%"
          borderRadius={{ base: '12px 12px 0 0', tablet: '12px' }}
          padding={6}
          overflow="hidden"
          backgroundColor="generic.0"
        >
          <Flex justifyContent="space-between" alignItems="flex-end" marginBottom={8} width="100%">
            <Text textStyle="heading.h6" fontWeight="semibold" letterSpacing="-0.4px" color="neutral.800">
              Track an Order
            </Text>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              paddingRight="0"
              type="button"
              _hover={{ opacity: 0.7 }}
            >
              <LucideIcon name="X" boxSize={4} />
            </Button>
          </Flex>

          <Flex
            flexDirection={{ base: 'column', tablet: 'row' }}
            gap={5}
            width="100%"
            as="form"
            onSubmit={handleTrackOrder}
          >
            <VStack flex={1} alignItems="stretch" gap={6} minWidth="0">
              <VStack alignItems="stretch" gap={2}>
                <Text textStyle="heading.h6.mobile" fontWeight="medium" color="neutral.900">
                  Track your guest order
                </Text>
                <Text textStyle="paragraph.small" fontWeight="normal" color="neutral.400">
                  Check your delivery status by entering your email and order number below.
                </Text>
              </VStack>

              <VStack alignItems="stretch" gap={3}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address"
                  height="48px"
                  paddingX={3}
                  paddingY={4}
                  textStyle="label.medium"
                  fontWeight="medium"
                  borderColor="neutral.100"
                  borderRadius="4px"
                  _placeholder={{
                    color: 'neutral.300',
                    fontWeight: 'medium',
                  }}
                  required
                />

                <Input
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="Order Number"
                  height="48px"
                  paddingX={3}
                  paddingY={4}
                  textStyle="label.medium"
                  fontWeight="medium"
                  borderColor="neutral.100"
                  borderRadius="4px"
                  _placeholder={{
                    color: 'neutral.300',
                    fontWeight: 'medium',
                  }}
                  required
                />

                <Button
                  type="submit"
                  height="50px"
                  width="100%"
                  backgroundColor={isFormValid ? 'primary.600' : 'primary.75'}
                  color={isFormValid ? 'generic.0' : 'neutral.300'}
                  textStyle="label.large"
                  fontWeight="medium"
                  borderRadius="8px"
                  disabled={!isFormValid}
                  _hover={{
                    backgroundColor: 'primary.500',
                  }}
                  _active={{
                    backgroundColor: 'primary.700',
                  }}
                  _disabled={{
                    cursor: 'not-allowed',
                    opacity: 1,
                  }}
                >
                  Check Order Status
                </Button>
              </VStack>
            </VStack>

            <Flex
              display={{ base: 'none', tablet: 'flex' }}
              width="1px"
              backgroundColor="neutral.75"
              alignSelf="stretch"
              flexShrink={0}
            />
            <Flex
              display={{ base: 'flex', tablet: 'none' }}
              height="1px"
              backgroundColor="neutral.75"
              width="100%"
              flexShrink={0}
            />

            <VStack flex={1} alignItems="stretch" gap={6} justifyContent="flex-start" minWidth="0">
              <VStack alignItems="stretch" gap={2}>
                <Text textStyle="heading.h6.mobile" fontWeight="medium" color="neutral.900">
                  Sign in to see all your orders
                </Text>
                <Text textStyle="paragraph.small" fontWeight="normal" color="neutral.400">
                  View your purchase history, track deliveries, and enjoy your SkinSociety rewards — all in one place.
                </Text>
              </VStack>

              <Button
                onClick={() => {
                  window.location.href = '/my-account/';
                }}
                height="50px"
                width="100%"
                backgroundColor="generic.100"
                color="generic.0"
                textStyle="label.large"
                fontWeight="normal"
                borderRadius="8px"
                _hover={{
                  backgroundColor: 'neutral.800',
                }}
                _active={{
                  backgroundColor: 'neutral.900',
                }}
              >
                Sign In
              </Button>
            </VStack>
          </Flex>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

export default TrackOrderModal;
